import { useState, type FormEvent } from 'react'
import { Box, TextField, Button, Typography, Alert, Link as MuiLink } from '@mui/material'
import { Link, useSearchParams } from 'react-router-dom'
import { DASHBOARD_AUTH_PATHS, type APIResponse } from '@yourownmetric/shared'

import { apiSlice } from '@/apis/apiSlice'

const resetPasswordApi = apiSlice.injectEndpoints({
    endpoints: builder => ({
        resetPassword: builder.mutation<{ message: string }, { token: string; password: string }>({
            query: body => ({
                url: '/auth/reset-password',
                method: 'POST',
                body,
            }),
            transformResponse: (res: APIResponse<{ message: string }>) => res.data!,
        }),
    }),
})

const { useResetPasswordMutation } = resetPasswordApi

/** Target of the emailed reset link: `/reset-password?token=…`. */
export function ResetPasswordPage() {
    const [searchParams] = useSearchParams()
    const token = searchParams.get('token') ?? ''
    const [password, setPassword] = useState('')
    const [confirm, setConfirm] = useState('')
    const [done, setDone] = useState(false)
    const [resetPassword, { isLoading, error }] = useResetPasswordMutation()

    const mismatch = confirm.length > 0 && password !== confirm

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        if (mismatch) {
            return
        }
        try {
            await resetPassword({ token, password }).unwrap()
            setDone(true)
        } catch {
            // error handled by RTK Query
        }
    }

    if (!token) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'center' }}>
                <Typography variant="h4">Invalid reset link</Typography>
                <Typography variant="body1" color="text.secondary">
                    This link is missing its token. Request a new one.
                </Typography>
                <MuiLink component={Link} to={DASHBOARD_AUTH_PATHS.forgotPassword} variant="body2">
                    Request a new reset link
                </MuiLink>
            </Box>
        )
    }

    if (done) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'center' }}>
                <Typography variant="h4">Password updated</Typography>
                <Typography variant="body1" color="text.secondary">
                    You can now sign in with your new password.
                </Typography>
                <Button component={Link} to={DASHBOARD_AUTH_PATHS.login} variant="contained">
                    Sign in
                </Button>
            </Box>
        )
    }

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400, mx: 'auto' }}
        >
            <Typography variant="h4" textAlign="center">
                Choose a new password
            </Typography>

            {error && (
                <Alert severity="error">
                    This reset link is invalid or has expired.{' '}
                    <MuiLink component={Link} to={DASHBOARD_AUTH_PATHS.forgotPassword}>
                        Request a new one
                    </MuiLink>
                    .
                </Alert>
            )}

            <TextField
                label="New password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                fullWidth
                autoComplete="new-password"
                inputProps={{ minLength: 8, maxLength: 128 }}
                helperText="At least 8 characters"
            />
            <TextField
                label="Confirm password"
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
                fullWidth
                autoComplete="new-password"
                error={mismatch}
                helperText={mismatch ? 'Passwords do not match' : ' '}
            />

            <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading || mismatch}
                fullWidth
            >
                {isLoading ? 'Saving…' : 'Set new password'}
            </Button>

            <Typography variant="body2" textAlign="center">
                <MuiLink component={Link} to={DASHBOARD_AUTH_PATHS.login}>
                    Back to Sign In
                </MuiLink>
            </Typography>
        </Box>
    )
}
